import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import ViewController from "../views/ViewController.vue";
import Register from "../views/Register.vue";
import SignIn from "../views/Signin.vue";
import EnterPin from "../views/EnterPin.vue";
import Error from "../views/Error.vue";
import Scanner from "../views/Scanner.vue";
import DeviceDetection from "../views/DeviceDetection.vue";
import Permissions from "../views/Permissions.vue";

import Wallet from "../views/Wallet.vue";
import Map from "../views/Map.vue";
import AR from "../views/AR.vue";
import Game from "../views/Game.vue";
import { nextTick } from "vue";
import CampaignPaused from "@/views/CampaignPaused.vue";
import OutOfPlays from "@/views/OutOfPlays.vue";
import { gtmService } from "@/utils/gtm";
import Prize from "@/views/Prize.vue";
import moment from "moment";
import { jwtDecode } from "jwt-decode";

import { authStore } from "@/store/pinia_modules/authStore";
import { campaignStore } from "@/store/pinia_modules/campaignStore";
import { derivePageObservability, OBSERVABILITY_EVENT_NAMES } from "@/utils/observability";
import { setRouter } from "@/utils/routerRef";
import { evaluateAuthBoundary, DecodedToken } from "@/utils/authBoundary";

const routes: Array<RouteRecordRaw> = [
	{
		path: "/error",
		name: "Error",
		component: Error,
	},
	{
		path: "/:campaign",
		name: "Register",
		component: Register,
	},
	{
		path: "/:campaign/sign-in",
		name: "SignIn",
		component: SignIn,
	},
	{
		path: "/:campaign/not-for-desktop",
		name: "DeviceDetection",
		component: DeviceDetection,
	},
	{
		path: "/:campaign/campaign-paused",
		name: "CampaignPaused",
		component: CampaignPaused,
	},
	{
		path: "/:campaign/out-of-plays",
		name: "OutOfPlays",
		component: OutOfPlays,
	},
	{
		path: "/:campaign/enter-pin",
		name: "Enter Pin",
		component: EnterPin,
	},
	{
		path: "/:campaign/vault",
		name: "Wallet",
		component: Wallet,
	},
	{
		path: "/:campaign/map",
		name: "Map",
		component: Map,
	},
	{
		path: "/:campaign/ar-viewer",
		name: "AR",
		component: AR,
	},
	{
		path: "/:campaign/game",
		name: "Game",
		component: Game,
	},
	{
		path: "/:campaign/scanner",
		name: "Scanner",
		component: Scanner,
	},
	{
		path: "/:campaign/permissions",
		name: "Permissions",
		component: Permissions,
	},
	{
		path: "/:campaign/prize/:vitemID",
		name: "Prize",
		component: Prize,
	},
	{
		path: "/:campaign/:page",
		name: "Custom",
		component: ViewController,
	},
];

// history: createWebHistory(import.meta.env.BASE_URL),

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
});

setRouter(router);

function isMobile(): boolean {
	const userAgent = navigator.userAgent || "";
	const touchCapableMac =
		/Macintosh/i.test(userAgent) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
	let isMobile =
		/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) ||
		touchCapableMac;
	const isDevMode = process.env.NODE_ENV == "development" ? true : false;
	if (isDevMode) {
		isMobile = true;
	}

	return isMobile;
}

router.beforeEach(async (to) => {
	if (!isMobile() && to.name !== "DeviceDetection") {
		return {
			name: "DeviceDetection",
			params: to.params,
			query: to.query,
		};
	}

	// Campaign start-date gate — IRL2-2898
	// If the campaign is already hydrated in the store (persisted pinia state or a
	// prior App.vue init in the same session) check startDate before any view mounts.
	// This is defence-in-depth alongside the App.vue check: the router guard fires
	// synchronously before the matched component renders, so there is zero flash of
	// the Register view even on SPA re-navigations.
	if (to.name !== "CampaignPaused" && to.name !== "DeviceDetection" && to.name !== "Error") {
		const campaign = campaignStore().getItem;
		if (campaign?.startDate) {
			if (moment().isBefore(moment(campaign.startDate))) {
				const campaignParam = to.params.campaign || campaign.id;
				if (campaignParam) {
					return {
						name: "CampaignPaused",
						params: { campaign: campaignParam },
					};
				}
			}
		}
	}

	// Auth-boundary gate — IRL2-2876 (no-token) + IRL2-2901 (cross-campaign
	// token bleed). Refuse navigation when the AccessToken is missing on an
	// authenticated route, OR is stamped for a DIFFERENT campaign than the
	// URL. The cross-campaign check now also covers "Custom" routes: the
	// how-to-play page is a Custom page, and the original 2876 gate exempted
	// the whole Custom family, so a 2nd-campaign how-to-play flashed before
	// the login screen (IRL2-2901). Decision logic lives in a pure, tested
	// helper. Fires synchronously before any component renders, so there is
	// zero flash of a post-auth page against mismatched state.
	const boundaryRedirect = evaluateAuthBoundary({
		routeName: typeof to.name === "string" ? to.name : null,
		navCampaignId: typeof to.params.campaign === "string" ? to.params.campaign : undefined,
		activeCampaignId: campaignStore().getItem?.id,
		accessToken: localStorage.getItem("AccessToken"),
		decodeToken: (token) => jwtDecode(token) as DecodedToken,
	});
	if (boundaryRedirect) {
		return boundaryRedirect;
	}

	nextTick(() => {
		const title = document.querySelector("#site-title");
		if (title) {
			const text = title.textContent;
			if (text) {
				document.title = text;
			}
		}
	});
});

// GTM page tracking
router.afterEach((to, from) => {
	if (to.fullPath === from.fullPath) {
		return;
	}

	nextTick(() => {
		// Track page view in GTM
		gtmService.pushEvent('page_view', {
			page_title: document.title,
			page_location: window.location.href,
			page_path: to.path,
			campaign_id: to.params.campaign,
			from_page: from.path
		});

		// Track page view in Backend
		const campaignId = to.params.campaign;
		if (campaignId && typeof campaignId === 'string') {
			const store = authStore();
			store.ensureCorrelationIds();
			// use userUUID if available, otherwise try userId, otherwise 'anonymous'
			// identifier is required by the API
			const identifier = store.getUserID || store.getUserUUID || 'anonymous';
			
			// include userId if available for better backend tracking
			const pageObservability = derivePageObservability({
				name: typeof to.name === "string" ? to.name : null,
				path: to.path,
			});
			const eventPayload: any = {
				campaignId: campaignId,
				eventName: OBSERVABILITY_EVENT_NAMES.navigationPageViewed,
				event: 'page-view',
				identifier: identifier,
				pageRoute: to.path,
				pageName: pageObservability.pageName,
				pageType: pageObservability.pageType,
				journeyStage: pageObservability.journeyStage,
				journeyStep: pageObservability.journeyStep,
				sessionId: store.getSessionID || undefined,
				journeyId: store.getJourneyID || undefined,
			};
			
			if (store.getUserID) {
				eventPayload.userId = store.getUserID;
			}

			store.dispatchUserEvent(eventPayload);
		}
	});
});

export default router;
